import { createAsyncThunk } from "@reduxjs/toolkit";
import type { AppDispatch, RootState } from "../../app/store";
import { refreshAccess, logout } from "../auth/authSlice";
import { TOO_MANY_REQUESTS } from "../../helpers/requestErrors";
// import type { AxiosError } from "axios"; // if you use axios

type ThunkCfg = {
  state: RootState;
  dispatch: AppDispatch;
  rejectValue: string;
};

type ThunkAPI = {
  dispatch: AppDispatch;
  getState: () => RootState;
  signal: AbortSignal;
  rejectWithValue: (v: string) => any;
};

/** Parse error, refresh on 401 once, then optionally retry the original request. */
async function errorParser<T>(
  err: unknown,
  thunkAPI: ThunkAPI,
  retryFn?: () => Promise<T>,
  alreadyRetried = false
): Promise<T> {
  // narrow the error shape carefully
  const status =
    (err as any)?.response?.status ?? (err as any)?.status ?? undefined;

  if (status === 403) {
    // Prefer a logout flow or a route action over hard redirect
    thunkAPI.dispatch(logout());
    throw normalizeErr(err);
  }

  if (status === 401 && !alreadyRetried) {
    try {
      // Optional: clear stale token from storage
      localStorage.removeItem("daex-auth-token");
      await thunkAPI.dispatch(refreshAccess()).unwrap();

      if (!retryFn) throw new Error("No retry function provided.");
      // Retry once
      return await retryFn();
    } catch (refreshErr) {
      throw normalizeErr(refreshErr);
    }
  }

  // Not 401 (or already retried once)
  throw normalizeErr(err);
}

function normalizeErr(e: unknown): Error {
  // Fixed value, so forms can show a proper "too many attempts" message
  const status = (e as any)?.response?.status ?? (e as any)?.status;
  if (status === 429) return new Error(TOO_MANY_REQUESTS);
  const msg =
    (e as any)?.response?.data?.message ?? (e as any)?.message ?? String(e);
  return new Error(msg);
}

/** Generic helper: create a thunk with auto refresh+retry on 401 */
export function createThunkWithError<Returned, Arg>(
  type: string,
  fn: (arg: Arg, thunkAPI: ThunkAPI) => Promise<Returned>
) {
  return createAsyncThunk<Returned, Arg, ThunkCfg>(
    type,
    async (arg, thunkAPI) => {
      try {
        // normal attempt
        return await fn(arg, thunkAPI as unknown as ThunkAPI);
      } catch (err) {
        try {
          // refresh+retry path. The retry calls fn directly, not errorParser,
          // so a second 401 is rejected below instead of looping
          return await errorParser(
            err,
            thunkAPI as unknown as ThunkAPI,
            () => fn(arg, thunkAPI as unknown as ThunkAPI),
            /*alreadyRetried*/ false
          );
        } catch (retryErr) {
          return thunkAPI.rejectWithValue(normalizeErr(retryErr).message);
        }
      }
    }
  );
}
